// Generic, schema-agnostic validation helpers shared by both Home wizards
// (public HomeFormFlow.jsx and BackOfficeHome.jsx). Mirrors the pattern
// already built for the Commercial wizard in
// src/screens/Commercial/validations/commercialStepFields.js.
//
// Unlike Commercial, Home already exports its own per-step leaf-field list
// (`answersHomeFieldsBySteps`) from each wizard's own validations file
// (HomeFormFlow.validations.js / BackofficeHomeFormFlow.validations.js), so
// no step-keyed field-list is redefined here — only the generic helpers and
// a shared Italian label dictionary (superset of both files' fields).

// Fields that must never be marked touched via setValue — they're
// useFieldArrays (insured_guarantees, location_guarantees), and their own
// red/green feedback is handled reactively by GuaranteeValidationSummary /
// the per-card hasSuccess prop, independent of RHF's errors/touched.
export const NON_TOUCHABLE_FIELDS = ['insured_guarantees', 'location_guarantees'];

export const homeFieldLabels = {
  insurance_formula: 'Formula Assicurativa',
  gender: 'Genere',
  business_name: 'Ragione Sociale',
  date_of_birth: 'Data di Nascita',
  vat_number: 'Partita IVA',
  company_type: 'Tipo Azienda',
  commune_of_birth_code: 'Comune di Nascita',
  province_of_birth_code: 'Provincia di Nascita',
  born_abroad: "Nato all'Estero",
  residence_commune_code: 'Comune di Residenza',
  residence_province_code: 'Provincia di Residenza',
  postal_code: 'CAP',
  house_number: 'Numero Civico',
  address: 'Indirizzo',
  name: 'Nome',
  surname: 'Cognome',
  email: 'Email',
  phone: 'Telefono',
  policy_effective_date: 'Data di Decorrenza',
  fiscal_code: 'Codice Fiscale',
  country_of_birth_code: 'Stato di Nascita',
  insured_guarantees: 'Garanzie Assicurato',
  house_type: 'Tipologia Abitazione',
  housing_type: 'Tipologia Abitativa',
  floor_level: 'Piano',
  presence_of_condominium_policy: 'Polizza Condominiale',
  residential_use_types: 'Uso Abitazione',
  construction_feature_types: 'Caratteristiche Costruttive',
  construction_year: 'Anno di Costruzione',
  interested_in_building_fire_coverage: 'Copertura Incendio Fabbricato',
  interested_in_contents_fire_coverage: 'Copertura Incendio Contenuto',
  building_value: 'Valore Fabbricato',
  insured_amount_contents_fire: 'Valore Assicurato Contenuto',
  owner_typology: 'Tipologia Proprietario',
  square_meters: 'Superficie',
  current_address: 'Indirizzo Abitazione',
  current_house_number: 'Numero Civico Abitazione',
  current_postal_code: 'CAP Abitazione',
  house_province_code: 'Provincia Abitazione',
  house_commune_code: 'Comune Abitazione',
  location_guarantees: 'Garanzie Ubicazione',
};

// Dot-path aware getter — Home's fields are flat today, but this keeps the
// helper consistent with Commercial's and future-proof for nested fields.
export function getFieldByPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

// The one correct isValid implementation — requires both "no error" and
// "touched", dot-path aware so it works for both wizards.
export function isHomeFieldValid(errors, touchedFields, name) {
  if (getFieldByPath(errors, name)?.message !== undefined) {
    return false;
  }
  if (getFieldByPath(touchedFields, name) === undefined) {
    return false;
  }
  return true;
}

// Synchronous, independent of react-hook-form's (async) internal state
// update timing — trigger()'s returned promise can resolve before RHF's own
// setState has actually re-rendered the component, so reading the
// component's `errors` closure right after `await trigger(...)` can be
// stale. Re-running the same yup schema against the same formData snapshot
// used for `schema.isValidSync(formData)` sidesteps that entirely.
export function getBlockingFields(schema, formData, stepFields) {
  try {
    schema.validateSync(formData, { abortEarly: false });
    return [];
  } catch (err) {
    const errorPaths = new Set((err.inner || []).map((innerError) => innerError.path));
    return (stepFields || []).filter((field) => errorPaths.has(field));
  }
}

export function buildMissingFieldsMessage(blockingFields) {
  if (!blockingFields || blockingFields.length === 0) {
    return 'Per favore, completa i campi richiesti correttamente.';
  }

  const names = blockingFields.map((field) => homeFieldLabels[field] || field);
  const shown = names.slice(0, 3).join(', ');
  const rest = names.length > 3 ? ` e altri ${names.length - 3}` : '';

  return `Per favore, completa correttamente i seguenti campi: ${shown}${rest}.`;
}
